// src/pages/teacher/TeacherSelectKindergarten.tsx
// בחירת גן — כשהגננת משויכת לכמה גנים

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { authApi } from '../../services/api';
import { Building2, MapPin, ChevronLeft } from 'lucide-react';

interface KindergartenOption {
  publicId: string;
  name: string;
  code: string;
  city: string;
}

function parseJwtClaims(token: string): Record<string, any> {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return {};
    const payload = atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(payload);
  } catch {
    return {};
  }
}

import { teacherColors } from '../../styles/colors';

const colors = {
  ...teacherColors,
  background: '#faf5ff',
};

export default function TeacherSelectKindergarten() {
  const navigate = useNavigate();
  const { user, setUser, setToken, setRefreshToken, setTokenExpiresAt } = useAuthStore();
  const [kindergartens, setKindergartens] = useState<KindergartenOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    document.title = 'KipiKid - בחירת גן';
    const stored = sessionStorage.getItem('teacher-kindergartens');
    if (stored) {
      try {
        setKindergartens(JSON.parse(stored));
      } catch {
        navigate('/teacher/login');
      }
    } else {
      navigate('/teacher/login');
    }
  }, [navigate]);

  const handleSelect = async (kg: KindergartenOption) => {
    setIsLoading(true);
    setError('');

    try {
      const result = await authApi.teacherSelectKg(kg.publicId);

      if (result.success && result.data) {
        const data = result.data;

        setToken(data.accessToken);
        setRefreshToken(data.refreshToken);
        setTokenExpiresAt(data.expiresAt);
        setUser({
          ...user!,
          kindergartenPublicId: kg.publicId,
          kindergartenName: kg.name,
        });

        sessionStorage.removeItem('teacher-kindergartens');
        navigate('/teacher/dashboard');
      } else {
        setError(result.message || 'שגיאה בבחירת גן');
      }
    } catch (err: any) {
      setError('שגיאת תקשורת');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div dir="rtl" style={{
      minHeight: '100vh',
      backgroundColor: colors.background,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
    }}>
      <div style={{
        width: '100%',
        maxWidth: '400px',
        backgroundColor: colors.white,
        borderRadius: '16px',
        padding: '32px 24px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
      }}>
        <h2 style={{
          fontSize: '22px',
          fontWeight: 'bold',
          color: colors.text,
          textAlign: 'center',
          marginBottom: '8px',
        }}>
          בחרי גן
        </h2>
        <p style={{
          fontSize: '14px',
          color: colors.textLight,
          textAlign: 'center',
          marginBottom: '24px',
        }}>
          שלום {user?.firstName || user?.name}, את משויכת לכמה גנים
        </p>

        {error && (
          <div style={{
            padding: '10px 14px',
            backgroundColor: '#fee2e2',
            borderRadius: '8px',
            marginBottom: '16px',
          }}>
            <p style={{ fontSize: '14px', color: '#ef4444', margin: 0 }}>{error}</p>
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {kindergartens.map(kg => (
            <button
              key={kg.publicId}
              onClick={() => handleSelect(kg)}
              disabled={isLoading}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '16px',
                backgroundColor: colors.white,
                border: `2px solid ${colors.border}`,
                borderRadius: '12px',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                textAlign: 'right',
                transition: 'all 0.2s',
                opacity: isLoading ? 0.7 : 1,
              }}
              onMouseEnter={e => {
                (e.currentTarget as HTMLElement).style.borderColor = colors.primary;
                (e.currentTarget as HTMLElement).style.backgroundColor = colors.primaryLight;
              }}
              onMouseLeave={e => {
                (e.currentTarget as HTMLElement).style.borderColor = colors.border;
                (e.currentTarget as HTMLElement).style.backgroundColor = colors.white;
              }}
            >
              <div style={{
                width: '44px',
                height: '44px',
                borderRadius: '12px',
                backgroundColor: colors.primaryLight,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}>
                <Building2 size={22} color={colors.primary} />
              </div>
              <div style={{ flex: 1 }}>
                <p style={{ fontSize: '16px', fontWeight: '600', color: colors.text, margin: 0 }}>
                  {kg.name}
                </p>
                {kg.city && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '4px' }}>
                    <MapPin size={12} color={colors.textLight} />
                    <span style={{ fontSize: '12px', color: colors.textLight }}>{kg.city}</span>
                  </div>
                )}
              </div>
              <ChevronLeft size={20} color={colors.textLight} />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
